import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatPrice } from "@/components/ListingCard";
import { MapPin, Bed, Bath, Ruler, ChevronLeft } from "lucide-react";

const ListingDetail = () => {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [idx, setIdx] = useState(0);
  const [inquiry, setInquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/public/listings/${id}`).then((r) => setL(r.data)).catch(() => setL(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/inquiry", { ...inquiry, listing_id: id, category: l?.category || "general" });
      setSent(true);
      setInquiry({ name: "", email: "", phone: "", message: "" });
    } catch {}
  };

  if (l === null) return <div className="p-20 text-center text-slate-500">Loading...</div>;
  if (l === false) return <div className="p-20 text-center text-slate-500">Listing not found.</div>;

  const isConfidential = l.category === "dental" || l.category === "medical";
  const mapQ = encodeURIComponent(l.address ? `${l.address}, ${l.location}` : l.location);

  return (
    <div className="bg-white" data-testid="listing-detail">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <Link to="/listings" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy" data-testid="back-to-listings">
          <ChevronLeft className="w-4 h-4" /> Back to listings
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="overline text-gold">{l.location}</div>
        <h1 className="font-display text-3xl lg:text-5xl text-navy mt-3 max-w-4xl">{l.title}</h1>
        <div className="mt-4 flex flex-wrap gap-6 items-center text-sm text-slate-600">
          <span className="font-display text-3xl text-navy">{formatPrice(l.price)}</span>
          {!isConfidential && l.bedrooms > 0 && (<span className="inline-flex items-center gap-1.5"><Bed className="w-4 h-4" />{l.bedrooms} beds</span>)}
          {!isConfidential && l.bathrooms > 0 && (<span className="inline-flex items-center gap-1.5"><Bath className="w-4 h-4" />{l.bathrooms} baths</span>)}
          {l.sqft > 0 && (<span className="inline-flex items-center gap-1.5"><Ruler className="w-4 h-4" />{l.sqft.toLocaleString()} sqft</span>)}
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{l.address ? `${l.address}, ${l.location}` : l.location}</span>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
              <img src={l.images?.[idx] || l.images?.[0]} alt={l.title} className="w-full h-full object-cover" />
            </div>
            {l.images?.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {l.images.map((im, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`aspect-[4/3] overflow-hidden border-2 ${i === idx ? "border-gold" : "border-transparent"}`} data-testid={`listing-thumb-${i}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <aside className="bg-slate-50 border border-slate-200 p-6 lg:p-8">
            <div className="overline text-gold">Request information</div>
            <h3 className="font-display text-2xl text-navy mt-2">Speak with an advisor</h3>
            {sent ? (
              <div className="mt-6 p-4 bg-white border border-gold text-navy text-sm" data-testid="inquiry-success">Thank you — an advisor will reach out within 24 hours.</div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="inquiry-form">
                <input required placeholder="Full name" value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gold" data-testid="inquiry-name" />
                <input required type="email" placeholder="Email" value={inquiry.email} onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gold" data-testid="inquiry-email" />
                <input placeholder="Phone" value={inquiry.phone} onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gold" data-testid="inquiry-phone" />
                <textarea required rows="4" placeholder="Your message" value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-gold" data-testid="inquiry-message" />
                <button className="btn-navy w-full" data-testid="inquiry-submit">Send Inquiry</button>
              </form>
            )}
          </aside>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="overline text-gold">About this property</div>
          <h3 className="font-display text-3xl text-navy mt-3">Description</h3>
          <p className="mt-6 text-slate-700 leading-relaxed text-lg">{l.description}</p>
          {isConfidential && (
            <div className="mt-8 p-4 bg-navy text-white text-sm border-l-4 border-gold">
              This is a confidential healthcare listing. Full details are provided under NDA only.
            </div>
          )}
        </div>
        <div>
          <div className="overline text-gold">Location</div>
          <h3 className="font-display text-3xl text-navy mt-3 mb-6">{l.location}</h3>
          <div className="aspect-[16/10] border border-slate-200">
            <iframe
              title="map"
              width="100%" height="100%"
              style={{ border: 0 }}
              loading="lazy"
              src={`https://maps.google.com/maps?q=${mapQ}&t=m&z=12&output=embed`}
              data-testid="listing-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListingDetail;
